import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { filter, map, startWith } from 'rxjs';
import { RoleService } from '../../services/role.service';

type PersonalTab = {
  key: 'jobs' | 'applications' | 'documents' | 'alert' | 'chats';
  link: string;
  paths: string[];
  newWindow?: boolean;
};

export const PERSONAL_TABS: PersonalTab[] = [
  { key: 'jobs', link: '/jobs', paths: ['/jobs'] },
  {
    key: 'applications',
    link: '/applications/my',
    paths: ['/applications/my', '/applications/my/archived'],
  },
  {
    key: 'documents',
    link: '/applications/my/documents',
    paths: ['/applications/my/documents'],
  },
  { key: 'alert', link: '/search-agent', paths: ['/search-agent'] },
  {
    key: 'chats',
    link: '/stellenchats',
    paths: ['/stellenchats'],
    newWindow: true,
  },
];

/** The tab of a personal job search page, or null on any other page. */
export function personalTabFor(url: string): PersonalTab | null {
  const path = url.split(/[?#]/)[0].replace(/\/$/, '');
  return PERSONAL_TABS.find(tab => tab.paths.includes(path)) ?? null;
}

/**
 * Prototype SE 01–11: school admins search jobs for themselves on the same pages as
 * a Fachkraft. Above those pages they see a title, a privacy note and the tabs.
 */
@Component({
  selector: 'app-personal-job-search-frame',
  templateUrl: './personal-job-search-frame.component.html',
  imports: [RouterLink, MatIcon, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class PersonalJobSearchFrameComponent {
  private readonly router = inject(Router);
  private readonly roleService = inject(RoleService);

  readonly tabs = PERSONAL_TABS;

  private readonly url = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );

  readonly activeTab = computed(() => personalTabFor(this.url()));

  readonly visible = computed(
    () => this.roleService.roleSignal() !== 'fachkraft' && !!this.activeTab()
  );

  readonly organisationName = computed(
    () => this.roleService.activeContextSignal()?.name ?? ''
  );
}
